import React, { useState } from 'react';

/**
 * SignInBlock — the shared magic-link email form.
 * Used by shell Home (anonymous section) and /login so the two never drift.
 *
 * Owns only the input + sending state. The parent decides what "sent" looks
 * like: `onSubmit(email)` performs the request (throw to signal failure),
 * `onSent(email)` fires after success so the parent can flip its own copy.
 *
 * Errors: pass `onError(err, message)` to handle them yourself (e.g. toast);
 * omit it and the block renders the message inline. `errorFallback` overrides
 * the default message when the API gives none.
 */
const SignInBlock = ({
  onSubmit,
  onSent,
  onError,
  buttonLabel = 'Send the link',
  sendingLabel = 'Sending…',
  placeholder = 'you@example.com',
  errorFallback = 'Could not send the link — try again',
  style,
}) => {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setSending(true);
    setError('');
    try {
      await onSubmit(trimmed);
      if (onSent) onSent(trimmed);
    } catch (err) {
      const message = err?.response?.data?.error || errorFallback;
      if (onError) onError(err, message);
      else setError(message);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <form onSubmit={submit} style={{ display: 'flex', gap: 10, ...style }}>
        <input
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          style={{ flex: 1, minWidth: 0, background: 'var(--ft-surface)', border: 'none', borderRadius: 'var(--ft-r-input)', padding: '14px 16px', fontSize: 16, outline: 'none' }}
        />
        <button type="submit" className="ft-btn" disabled={sending}>
          {sending ? sendingLabel : buttonLabel}
        </button>
      </form>
      {error && (
        <p style={{ fontSize: 13.5, color: '#FF3B30', margin: '10px 2px 0' }}>{error}</p>
      )}
    </>
  );
};

export default SignInBlock;
